import { useEffect } from "react";
import { canonicalCompetitionDisplayName } from "@sportex100/shared/utils/competitionIdentity.ts";
import type { PoolMembership } from "../types/pools";

export const SITE_URL = "https://sportex100.com";
export const DEFAULT_SEO_TITLE = "SportEx100 | Office Pool FIFA World Cup Championship";
export const DEFAULT_SEO_DESCRIPTION =
  "Run an office pool for the FIFA World Cup Championship with private picks, team markets, standings, and friend-group competition on SportEx100.";
export const DEFAULT_SHARE_IMAGE = "/img/sportex100-square.png";

type SeoRoute = "/" | "/market" | "/portfolio" | "/research" | "/standings" | string;

export type SeoMetadata = {
  title: string;
  description: string;
  canonicalUrl: string;
  imageUrl: string;
};

type PoolSeoContext = Pick<
  PoolMembership,
  "pool_name" | "competition_code" | "competition_name" | "season_code" | "pool_status"
>;

function compactText(value: string) {
  return value.replace(/\s+/g, " ").trim();
}

function safeText(value: string | null | undefined) {
  return compactText(value ?? "");
}

function safeCompetitionName(pool: PoolSeoContext | null | undefined) {
  const canonicalName = canonicalCompetitionDisplayName(pool?.competition_code, pool?.competition_name);
  return canonicalName || safeText(pool?.competition_name) || "current sports competition";
}

function routeLabel(pathname: SeoRoute) {
  if (pathname === "/market") return "Market";
  if (pathname === "/portfolio") return "Sportfolio";
  if (pathname === "/research") return "Picks";
  if (pathname === "/standings") return "Standings";
  return null;
}

function canonicalPath(pathname: SeoRoute) {
  if (pathname === "/market" || pathname === "/portfolio" || pathname === "/research" || pathname === "/standings") {
    return pathname;
  }
  return "/";
}

export function buildSeoMetadata(pathname: SeoRoute, pool?: PoolSeoContext | null): SeoMetadata {
  const path = canonicalPath(pathname);
  const label = routeLabel(path);
  const poolName = safeText(pool?.pool_name);
  const competitionName = safeCompetitionName(pool);
  const seasonCode = safeText(pool?.season_code);
  const competitionLabel = seasonCode ? `${competitionName} ${seasonCode}` : competitionName;

  if (!poolName) {
    const title = label ? `${label} | SportEx100` : DEFAULT_SEO_TITLE;
    const description = label
      ? `Preview SportEx100 ${label.toLowerCase()} for private sports pools: picks, team markets, standings, and friend competition in one place.`
      : DEFAULT_SEO_DESCRIPTION;
    return {
      title,
      description,
      canonicalUrl: `${SITE_URL}${path}`,
      imageUrl: `${SITE_URL}${DEFAULT_SHARE_IMAGE}`,
    };
  }

  const title = label ? `${poolName} ${label} | SportEx100` : `${poolName} | SportEx100`;
  const description = `Compete with friends in ${poolName}, a private ${competitionLabel} pool on SportEx100 with picks, team markets, standings, and live pool progress.`;

  return {
    title,
    description,
    canonicalUrl: `${SITE_URL}${path}`,
    imageUrl: `${SITE_URL}${DEFAULT_SHARE_IMAGE}`,
  };
}

function setMetaByName(name: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
}

function setMetaByProperty(property: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonicalLink(url: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  element.href = url;
}

export function applySeoMetadata(metadata: SeoMetadata) {
  document.title = metadata.title;
  setMetaByName("description", metadata.description);
  setMetaByName("twitter:card", "summary_large_image");
  setMetaByName("twitter:title", metadata.title);
  setMetaByName("twitter:description", metadata.description);
  setMetaByName("twitter:image", metadata.imageUrl);
  setMetaByProperty("og:site_name", "SportEx100");
  setMetaByProperty("og:type", "website");
  setMetaByProperty("og:title", metadata.title);
  setMetaByProperty("og:description", metadata.description);
  setMetaByProperty("og:url", metadata.canonicalUrl);
  setMetaByProperty("og:image", metadata.imageUrl);
  setCanonicalLink(metadata.canonicalUrl);
}

export function useSeoMetadata(pathname: SeoRoute, pool?: PoolSeoContext | null) {
  useEffect(() => {
    applySeoMetadata(buildSeoMetadata(pathname, pool));
  }, [pathname, pool?.competition_code, pool?.competition_name, pool?.pool_name, pool?.season_code]);
}
